import { useState, useRef, useEffect, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, MailCheck, KeyRound, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Capacitor } from '@capacitor/core';
import TelemetryLogo from '@/src/components/dashboard/logo';
import { supabase } from '@/src/lib/supabase';
import { recoveryStore } from '@/src/lib/recovery';
import { useTheme } from '@/src/contexts/ThemeContext';
import ThemeToggle from '@/src/components/ThemeToggle';

interface SignInProps {
  onSignIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: string | null }>;
  onSignUp?: (email: string, password: string, name: string, captchaToken?: string) => Promise<{ error: string | null; needsConfirmation: boolean }>;
  defaultEmail?: string;
  lockedEmail?: string;
  headline?: string;
  skipInviteCheck?: boolean;
  subhead?: string;
  startMode?: 'signin' | 'signup';
}

const RESEND_COOLDOWN = 60;

// Centralised colour tokens for this screen, driven by the active theme.
// Kept as plain inline-style values (rather than Tailwind `dark:` classes)
// so this doesn't depend on how/whether dark mode is wired up in the
// Tailwind config elsewhere in the app.
function useSignInColors() {
  const { isDark } = useTheme();
  return {
    isDark,
    bg: isDark ? '#0F0F0D' : '#FAF8F9',
    text: isDark ? 'rgba(255,255,255,0.9)' : '#1D1D18',
    textDim: isDark ? 'rgba(255,255,255,0.5)' : '#6E6E64',
    textFaint: isDark ? 'rgba(255,255,255,0.25)' : '#A6A69A',
    label: isDark ? '#A3A3A3' : '#6E6E64',
    inputBg: isDark ? '#171717' : '#FFFFFF',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
    inputBorderFocus: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)',
    placeholder: isDark ? '#525252' : '#A3A3A3',
    icon: isDark ? '#737373' : '#A3A3A3',
    accent: '#2E5A32',
    accentSoft: isDark ? '#8FD08F' : '#2E5A32',
    danger: isDark ? '#F87171' : '#DC2626',
    modalBg: isDark ? '#0A0A0A' : '#FFFFFF',
    modalBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  };
}

// The rings + dot mark on its own, recolourable via `color` so it reads
// correctly on both a dark background and a light one (the old
// /logo-icon.png asset was baked white-on-transparent for dark mode only).
function LogoMark({ size = 56, color }: { size?: number; color: string }) {
  return (
    <svg viewBox="0 0 40 40" fill="none" width={size} height={size}>
      <circle cx="20" cy="20" r="10.6" stroke={color} strokeWidth="3.3" strokeDasharray="6.66 1.665" strokeLinecap="butt" />
      <circle cx="20" cy="20" r="4.6" fill="#E8A020" />
    </svg>
  );
}

export default function SignIn({ onSignIn, onSignUp, defaultEmail, lockedEmail, headline, subhead, startMode, skipInviteCheck }: SignInProps) {
  const c = useSignInColors();
  const [mode, setMode] = useState<'signin' | 'signup'>(startMode ?? 'signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(lockedEmail ?? defaultEmail ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);       // sign-up code screen
  const [resetCodeSent, setResetCodeSent] = useState(false);   // password reset code screen
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [emailChecked, setEmailChecked] = useState(!!lockedEmail);
  const [resetting, setResetting] = useState(false);

  // Shared by both code screens (sign-up confirmation and password reset)
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [resendNote, setResendNote] = useState<string | null>(null);

  const captchaSiteKey = import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined;
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);

  const isSignup = mode === 'signup' && !!onSignUp;

  // Countdown for the "Resend code" button
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  function friendlyCodeError(message: string) {
    const msg = message.toLowerCase();
    return msg.includes('expired') || msg.includes('invalid')
      ? 'That code is incorrect or has expired. Check the latest email, or tap "Resend code".'
      : message;
  }

  function openCodeScreen() {
    setCode('');
    setError(null);
    setResendNote(null);
    setResendIn(RESEND_COOLDOWN);
  }

  function sendResetCode() {
    // The email carries a code (see the Reset Password template). redirectTo only
    // matters for any old link-style emails still sitting in someone's inbox.
    const base = Capacitor.isNativePlatform() ? 'https://app.opsalo.com' : window.location.origin;
    return supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${base}/reset-password` });
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError('Enter your email first, then tap "Forgot password?".');
      return;
    }
    setResetting(true);
    setError(null);
    const { error: err } = await sendResetCode();
    setResetting(false);
    if (err) { setError(err.message); return; }
    openCodeScreen();
    setResetCodeSent(true);
  }

  async function handleVerifySignup(e: FormEvent) {
    e.preventDefault();
    if (code.length < 6) return;
    setVerifying(true);
    setError(null);
    setResendNote(null);
    const { error: err } = await supabase.auth.verifyOtp({ email: email.trim(), token: code, type: 'signup' });
    setVerifying(false);
    if (err) { setError(friendlyCodeError(err.message)); return; }
    // Success: Supabase now holds a session and App routes into onboarding.
  }

  async function handleVerifyReset(e: FormEvent) {
    e.preventDefault();
    if (code.length < 6) return;
    setVerifying(true);
    setError(null);
    setResendNote(null);
    // Arm first, so the session this creates opens "Set a new password"
    // rather than the dashboard.
    recoveryStore.arm();
    const { error: err } = await supabase.auth.verifyOtp({ email: email.trim(), token: code, type: 'recovery' });
    if (err) {
      recoveryStore.disarm();
      setVerifying(false);
      setError(friendlyCodeError(err.message));
      return;
    }
    // Success: App switches to the ResetPassword screen.
  }

  async function handleResend() {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setError(null);
    setResendNote(null);
    const { error: err } = resetCodeSent
      ? await sendResetCode()
      : await supabase.auth.resend({ type: 'signup', email: email.trim() });
    setResending(false);
    if (err) { setError(err.message); return; }
    setCode('');
    setResendIn(RESEND_COOLDOWN);
    setResendNote('New code sent. Only the latest code will work.');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    // Step 1: for signup, check email first
    if (isSignup && !emailChecked && !skipInviteCheck) {
      setSubmitting(true);
      setError(null);
      const { data: invites } = await supabase
        .from('invites')
        .select('token')
        .eq('email', email.trim().toLowerCase())
        .eq('status', 'pending');
      setSubmitting(false);
      if (invites && invites.length > 0) {
        setError('You have a pending invite. Please use the invite link sent to your email to join your venue.');
        return;
      }
      setEmailChecked(true);
      return;
    }

    if (!password) return;
    if (isSignup && !name.trim()) { setError('Enter your name.'); return; }
    if (captchaSiteKey && !captchaToken) { setError('Please complete the captcha.'); return; }
    setSubmitting(true);
    setError(null);
    const token = captchaToken ?? undefined;
    const resetCaptcha = () => { captchaRef.current?.resetCaptcha(); setCaptchaToken(null); };

    if (isSignup && onSignUp) {
      const { error: err, needsConfirmation } = await onSignUp(email.trim(), password, name.trim(), token);
      setSubmitting(false);
      if (err) { setError(err); resetCaptcha(); return; }
      if (needsConfirmation) {
        openCodeScreen();
        setConfirmSent(true);
        return;
      }
    } else {
      const { error: err } = await onSignIn(email.trim(), password, token);
      setSubmitting(false);
      if (err) { setError(err); resetCaptcha(); }
    }
  }

  // Shared layout for both code screens
  function renderCodeScreen(opts: {
    icon: React.ReactNode;
    title: string;
    body: React.ReactNode;
    submitLabel: string;
    onSubmit: (e: FormEvent) => void;
    backLabel: string;
    onBack: () => void;
  }) {
    const disabled = verifying || code.length < 6;
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center px-6" style={{ background: c.bg }}>
        <ThemeToggle fixed />
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm text-center">
          {opts.icon}
          <h1 className="text-xl font-semibold" style={{ color: c.text }}>{opts.title}</h1>
          <p className="text-sm mt-2" style={{ color: c.textDim }}>{opts.body}</p>

          <form onSubmit={opts.onSubmit} className="mt-6 space-y-3">
            <input
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 8)); if (error) setError(null); }}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="000000"
              aria-label="Verification code"
              className="w-full h-14 rounded-xl text-center text-2xl font-semibold tracking-[0.4em] focus:outline-none transition-colors"
              style={{ background: c.inputBg, border: `1px solid ${c.inputBorder}`, color: c.text }}
              onFocus={(e) => { e.currentTarget.style.borderColor = c.inputBorderFocus; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = c.inputBorder; }}
            />

            {error && (
              <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] px-1" style={{ color: c.danger }}>{error}</motion.p>
            )}
            {resendNote && !error && (
              <p className="text-[11px] px-1" style={{ color: c.accentSoft }}>{resendNote}</p>
            )}

            <motion.button
              type="submit"
              disabled={disabled}
              whileTap={{ scale: 0.97 }}
              className="w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:cursor-not-allowed transition-colors"
              style={{
                background: disabled ? (c.isDark ? 'rgba(255,255,255,0.08)' : '#E7E5E0') : c.accent,
                color: disabled ? c.textFaint : 'white',
              }}
            >
              {verifying ? 'Verifying…' : opts.submitLabel}
              {!verifying && <ArrowRight size={16} />}
            </motion.button>
          </form>

          <button
            type="button"
            onClick={handleResend}
            disabled={resendIn > 0 || resending}
            className="mt-5 text-xs disabled:opacity-60"
            style={{ color: c.accentSoft }}
          >
            {resending ? 'Sending…' : resendIn > 0 ? `Resend code in ${resendIn}s` : "Didn't get it? Resend code"}
          </button>

          <div>
            <button
              type="button"
              onClick={opts.onBack}
              className="mt-4 text-xs font-bold uppercase tracking-widest"
              style={{ color: c.textDim }}
            >
              {opts.backLabel}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (confirmSent) {
    return renderCodeScreen({
      icon: <MailCheck size={40} className="mx-auto mb-4" style={{ color: c.accentSoft }} />,
      title: 'Check your email',
      body: <>We sent a verification code to <span style={{ color: c.text }}>{email}</span>. Enter it below to finish creating your account. Can't see it? Check your junk or spam folder.</>,
      submitLabel: 'Verify and continue',
      onSubmit: handleVerifySignup,
      backLabel: 'Use a different email',
      onBack: () => { setConfirmSent(false); setCode(''); setError(null); setResendNote(null); setEmailChecked(!!lockedEmail); setPassword(''); },
    });
  }

  if (resetCodeSent) {
    return renderCodeScreen({
      icon: <KeyRound size={40} className="mx-auto mb-4" style={{ color: c.accentSoft }} />,
      title: 'Reset your password',
      body: <>We sent a reset code to <span style={{ color: c.text }}>{email}</span>. Enter it to choose a new password. Can't see it? Check your junk or spam folder.</>,
      submitLabel: 'Continue',
      onSubmit: handleVerifyReset,
      backLabel: 'Back to sign in',
      onBack: () => { setResetCodeSent(false); setCode(''); setError(null); setResendNote(null); },
    });
  }

  const isSubmitDisabled =
    submitting ||
    !email.trim() ||
    ((!isSignup || emailChecked) && !password) ||
    (!!captchaSiteKey && (!isSignup || emailChecked) && !captchaToken) ||
    (isSignup && emailChecked && !agreedToTerms);

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center px-6" style={{ background: c.bg }}>
      <ThemeToggle fixed />
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 24 }}
        className="w-full max-w-sm"
      >
        <div className="flex flex-col items-center gap-2 mb-10">
          {/* Icon only — just the rings + dot part of the logo */}
          <LogoMark size={110} color={c.text} />
          <p className="text-xl font-medium" style={{ color: c.text }}>OpsAlo</p>
          <p className="text-[11px] tracking-[0.2em] uppercase font-medium" style={{ color: c.accentSoft }}>
            {subhead ?? (isSignup ? 'Create your venue' : 'Operational intelligence')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" style={{ color: c.label }}>Email</label>
            <div className="relative">
              <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: c.icon }} />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (emailChecked) setEmailChecked(false); }}
                autoComplete="email"
                disabled={!!lockedEmail}
                placeholder="you@venue.com"
                className="w-full h-12 rounded-xl pl-11 pr-4 text-sm focus:outline-none transition-colors disabled:opacity-60"
                style={{ background: c.inputBg, border: `1px solid ${c.inputBorder}`, color: c.text }}
                onFocus={(e) => { e.currentTarget.style.borderColor = c.inputBorderFocus; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = c.inputBorder; }}
              />
            </div>
          </div>

          {(!isSignup || emailChecked) && (
            <>
              {isSignup && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium" style={{ color: c.label }}>Your name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    placeholder="Alex Smith"
                    className="w-full h-12 rounded-xl px-4 text-sm focus:outline-none transition-colors"
                    style={{ background: c.inputBg, border: `1px solid ${c.inputBorder}`, color: c.text }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = c.inputBorderFocus; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = c.inputBorder; }}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-sm font-medium" style={{ color: c.label }}>Password</label>
                <div className="relative">
                  <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: c.icon }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                    placeholder="Enter your password"
                    className="w-full h-12 rounded-xl pl-11 pr-11 text-sm focus:outline-none transition-colors"
                    style={{ background: c.inputBg, border: `1px solid ${c.inputBorder}`, color: c.text }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = c.inputBorderFocus; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = c.inputBorder; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                    style={{ color: c.icon }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {!isSignup && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resetting}
                    className="text-xs hover:underline disabled:opacity-60"
                    style={{ color: c.accentSoft }}
                  >
                    {resetting ? 'Sending code…' : 'Forgot password?'}
                  </button>
                </div>
              )}
            </>
          )}

          {error && (
            <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] px-1" style={{ color: c.danger }}>{error}</motion.p>
          )}

          {(!isSignup || emailChecked) && captchaSiteKey && (
            <div className="flex justify-center pt-1">
              <HCaptcha ref={captchaRef} sitekey={captchaSiteKey} theme={c.isDark ? 'dark' : 'light'} onVerify={(t) => setCaptchaToken(t)} onExpire={() => setCaptchaToken(null)} />
            </div>
          )}

          {isSignup && emailChecked && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0"
                style={{ accentColor: c.accent }}
              />
              <span className="text-[11px] leading-relaxed" style={{ color: c.textDim }}>
                I agree to the{' '}
                <a href="https://opsalo.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: c.accentSoft }}>Privacy Policy</a>
              </span>
            </label>
          )}

          <motion.button
            type="submit"
            disabled={isSubmitDisabled}
            whileTap={{ scale: 0.97 }}
            className="w-full h-12 mt-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:cursor-not-allowed transition-colors"
            style={{
              background: isSubmitDisabled ? (c.isDark ? 'rgba(255,255,255,0.08)' : '#E7E5E0') : c.accent,
              color: isSubmitDisabled ? c.textFaint : 'white',
            }}
          >
            {submitting
              ? (isSignup && !emailChecked ? 'Checking…' : isSignup ? 'Creating…' : 'Signing in…')
              : (isSignup && !emailChecked ? 'Continue' : isSignup ? 'Create account' : 'Sign in')}
            {!submitting && <ArrowRight size={16} />}
          </motion.button>
        </form>

        {onSignUp && (
          <button
            onClick={() => { setMode(isSignup ? 'signin' : 'signup'); setError(null); setEmailChecked(false); }}
            className="w-full text-center text-[11px] mt-6 tracking-wide"
          >
            {isSignup ? (
              <span style={{ color: c.textDim }}>Already have an account? <span style={{ color: c.accentSoft }}>Sign in</span></span>
            ) : (
              <span style={{ color: c.textDim }}>New here? <span style={{ color: c.accentSoft }}>Create an account</span></span>
            )}
          </button>
        )}

        <p className="text-[10px] text-center mt-6 tracking-[0.2em] uppercase font-medium" style={{ color: c.textFaint }}>OpsAlo</p>
      </motion.div>
    </div>
  );
}